import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Pin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDispatchSuggestions } from '../../core/CommandDispatcher';
import { useCommandInteract } from '../../core/commands/commandInteract';
import {
    executeDraftCommand,
    getAnchorPoint,
    getCursorGround,
    injectPointAtWorld,
    resolveDynamicPoint,
    setAnchorPoint,
} from '../../core/dynamicInput/dynamicInputEngine';
import {
    useDynamicInputStore,
    isDynamicInputActive,
    dynamicOwnsKeyEvent,
} from '../../state/useDynamicInputStore';
import { useDynBoxStore, isDynBoxEntryKey, type DynFieldLabel } from '../../state/useDynBoxStore';
import { useSnapStore } from '../../tools/snapping/SnapManager';
import { useAppStore } from '../../state/store';
import { isCommandActive } from '../../utils/commandCompletion';

/**
 * Dynamic Input Provider (2D DYN™ behavior).
 *
 * Mounted once per CAD workbench (2D / 3D). While enabled it owns
 * plain keystrokes at the document level (capture phase) and renders a
 * tooltip glued to the cursor where:
 *   - typing letters builds a command name (autocomplete inline,
 *     Tab = accept suggestion),
 *   - Space / Enter runs the command — same dispatch chain as the bottom
 *     command line — or completes the best suggestion when ambiguous,
 *   - typing a coordinate/distance value (`5,3`, `@2,4`, `5<30`, `5`)
 *     injects that exact point into the active tool,
 *   - a live readout shows the cursor position + polar distance/angle
 *     from the last base point.
 * F-keys, Ctrl/Alt combos and Delete keep flowing to the global shortcut
 * system untouched.
 */

const CURSOR_OFFSET = 20;        // tooltip gap from the pointer hotspot
const FEEDBACK_MS = 1500;        // how long a commit/error line lingers

function topSuggestionName(s: string): string {
    return s.split(' ')[0];
}

/** Does the buffer parse as a pure coordinate/distance entry? */
function looksLikePoint(buffer: string): boolean {
    return resolveDynamicPoint(buffer) !== null;
}

/** Live cursor-derived value of one DynBox field (never stored — derived). */
function liveFieldValue(label: DynFieldLabel): string | null {
    const cursor = getCursorGround();
    const anchor = getAnchorPoint();
    switch (label) {
        case 'X': return cursor ? cursor.x.toFixed(4) : null;
        case 'Y': return cursor ? cursor.y.toFixed(2) : null;
        case 'Z': return cursor ? cursor.z.toFixed(2) : null;
        case 'Distance':
            return cursor && anchor ? cursor.distanceTo(anchor).toFixed(4) : null;
        case 'Angle':
            return cursor && anchor
                ? `${((Math.atan2(cursor.y - anchor.y, cursor.x - anchor.x) * 180) / Math.PI).toFixed(2)}`
                : null;
        default:
            return null;
    }
}

export function DynamicInputProvider() {
    const { t } = useTranslation();
    const buffer = useDynamicInputStore((s) => s.buffer);
    const suggestions = useDynamicInputStore((s) => s.suggestions);
    const readout = useDynamicInputStore((s) => s.readout);
    const feedback = useDynamicInputStore((s) => s.feedback);
    const setBuffer = useDynamicInputStore((s) => s.setBuffer);
    const setSuggestions = useDynamicInputStore((s) => s.setSuggestions);
    const setFeedback = useDynamicInputStore((s) => s.setFeedback);
    const clearFeedback = useDynamicInputStore((s) => s.clearFeedback);
    const appendLog = useDynamicInputStore((s) => s.appendLog);
    const pushHistory = useDynamicInputStore((s) => s.pushHistory);
    // DynBox editing state (Faz 11.3): fields/locks live in their own store.
    const dynMode = useDynBoxStore((s) => s.mode);
    const dynFields = useDynBoxStore((s) => s.fields);
    const dynActiveIndex = useDynBoxStore((s) => s.activeIndex);
    const dynRelative = useDynBoxStore((s) => s.relative);
    const interactStepKind = useCommandInteract((st) =>
        st.active ? st.spec?.steps[st.stepIndex]?.kind ?? null : null
    );
    const interactStepIndex = useCommandInteract((st) => st.stepIndex);
    const interactPrompt = useCommandInteract((st) =>
        st.active ? (st.spec?.steps[st.stepIndex]?.prompt ?? null) : null
    );

    const tooltipRef = useRef<HTMLDivElement>(null);
    const historyScanRef = useRef(-1);

    // DynBox mode follows the active step: point steps show the X/Y pair
    // (plus the polar Distance/Angle pair once a base point exists), numeric
    // steps a single Scalar; pick steps keep the prompt head only.
    const dynamicEnabled = useAppStore((s) => s.interactionOptions.dynamicInput);
    useEffect(() => {
        const store = useDynBoxStore.getState();
        if (!dynamicEnabled || !interactStepKind) {
            store.stopEditing();
            return;
        }
        if (interactStepKind === 'point') {
            // A base point flips the entry set to the four-field layout (the
            // classic dimension input on subsequent points).
            store.startEditing(getAnchorPoint() ? 'point' : 'xy');
        } else if (interactStepKind === 'numeric') {
            store.startEditing('scalar');
        } else {
            store.stopEditing();
        }
        // stepIndex re-runs this after every commit (the anchor state changes
        // between steps, so the field set must be re-derived).
    }, [dynamicEnabled, interactStepKind, interactStepIndex]);

    // Tooltip follows the pointer without re-rendering React on each move.
    useEffect(() => {
        const handleMove = (e: PointerEvent) => {
            const el = tooltipRef.current;
            if (!el) return;
            const w = el.offsetWidth;
            const h = el.offsetHeight;
            let x = e.clientX + CURSOR_OFFSET;
            let y = e.clientY + CURSOR_OFFSET;
            // Keep the tooltip inside the window when it would clip the edges.
            if (x + w > window.innerWidth - 8) x = e.clientX - w - CURSOR_OFFSET;
            if (y + h > window.innerHeight - 8) y = e.clientY - h - CURSOR_OFFSET;
            el.style.transform = `translate(${x}px, ${y}px)`;
        };
        window.addEventListener('pointermove', handleMove);
        return () => window.removeEventListener('pointermove', handleMove);
    }, []);

    // Feedback lines fade out on their own.
    useEffect(() => {
        if (!feedback) return;
        const seq = feedback.seq;
        const timer = window.setTimeout(() => clearFeedback(seq), FEEDBACK_MS);
        return () => window.clearTimeout(timer);
    }, [feedback, clearFeedback]);

    // ---- action handlers -------------------------------------------------

    const refreshSuggestions = (value: string) => {
        setSuggestions(value.length >= 1 ? getDispatchSuggestions(value) : []);
    };

    const acceptSuggestion = () => {
        const list = useDynamicInputStore.getState().suggestions;
        if (list.length === 0) return;
        setBuffer(topSuggestionName(list[0]));
        setSuggestions([]);
    };

    /**
     * While a CommandInteract session is running, typed values feed its
     * current step directly (coordinates → point step, number/text → value
     * step) — exactly what the bottom command line does, so both entries
     * speak the same language.
     */
    const feedInteract = (value: string): boolean => {
        const interact = useCommandInteract.getState();
        if (!interact.active || !interact.spec) return false;
        const step = interact.spec.steps[interact.stepIndex];
        if (!step) return false;
        pushHistory(value);
        if (step.kind === 'point') {
            const resolved = resolveDynamicPoint(value);
            if (resolved && 'error' in resolved) {
                setFeedback(resolved.error, 'error');
                setBuffer('');
                return true;
            }
            if (resolved && 'point' in resolved) {
                interact.addPoint(resolved.point);
                setAnchorPoint(resolved.point);
                setFeedback(resolved.label, 'ok');
                setBuffer('');
                setSuggestions([]);
                return true;
            }
            const parts = value.split(',').map((v) => Number(v.trim()));
            if (parts.length >= 2 && parts.every((v) => Number.isFinite(v))) {
                const point = new THREE.Vector3(parts[0], parts[1], parts[2] ?? 0);
                interact.addPoint(point);
                setAnchorPoint(point);
                setFeedback('Point accepted', 'ok');
                setBuffer('');
                return true;
            }
            setFeedback('Coordinates as X,Y — or click in the viewport', 'error');
            return true;
        }
        const numeric = Number(value);
        if (step.kind === 'numeric' && Number.isFinite(numeric)) {
            interact.provideValue(numeric);
        } else {
            interact.provideValue(value);
        }
        setFeedback(step.prompt, 'ok');
        setBuffer('');
        setSuggestions([]);
        return true;
    };

    /**
     * DynBox commit (Faz 11.3): typed/locked fields compose the classic
     * entry grammar ("x,y" / "@dx,dy" / "d<a" / scalar) and ride the SAME
     * resolution path as typed input — feedInteract for the command engine,
     * commitPoint (injectPointAtWorld) for direct-tool entry. The injection
     * pipeline itself is untouched.
     */
    const commitDynBox = (): boolean => {
        const box = useDynBoxStore.getState();
        if (!box.mode) return false;
        const valueOf = (index: number): string | null => {
            const field = box.fields[index];
            if (!field) return null;
            if (field.typed !== null && field.typed !== '') return field.typed;
            if (field.locked) return field.typed;
            return liveFieldValue(field.label);
        };
        let raw: string | null = null;
        if (box.mode === 'point') {
            // Dimensional entry wins when any polar field carries a value:
            // the locked-angle case commits d<a with the live distance.
            const dTyped = box.fields[2]?.typed;
            const aTyped = box.fields[3]?.typed;
            if ((dTyped || aTyped) && getAnchorPoint()) {
                const d = valueOf(2);
                const a = valueOf(3);
                if (d && a) raw = `${d}<${a}`;
            } else {
                const x = valueOf(0);
                const y = valueOf(1);
                if (x && y) {
                    // Relative mode ('@' first char): typed values are deltas
                    // from the anchor (classic relative coordinate entry).
                    raw = box.relative && getAnchorPoint() ? `@${x},${y}` : `${x},${y}`;
                }
            }
        } else if (box.mode === 'xy') {
            const x = valueOf(0);
            const y = valueOf(1);
            if (x && y) {
                raw = box.relative && getAnchorPoint() ? `@${x},${y}` : `${x},${y}`;
            }
        } else {
            const scalar = box.fields[0]?.typed;
            if (scalar) raw = scalar;
        }
        if (!raw) return false;
        const handled = feedInteract(raw);
        if (!handled) {
            // No command engine waiting — inject into the active tool.
            if (looksLikePoint(raw)) commitPoint(raw);
        }
        // Fresh field set for the next step (the mode effect also re-runs
        // when the step advances).
        useDynBoxStore.getState().startEditing(box.mode);
        return true;
    };

    const commitPoint = (value: string): boolean => {
        if (!isCommandActive()) {
            setFeedback('No active command to receive the point', 'error');
            setBuffer('');
            setSuggestions([]);
            return true;
        }
        const resolved = resolveDynamicPoint(value);
        if (!resolved) return false;
        if ('error' in resolved) {
            setFeedback(resolved.error, 'error');
            return true;
        }
        if (!injectPointAtWorld(resolved.point)) {
            setFeedback('Viewport not ready', 'error');
            return true;
        }
        setFeedback(resolved.label, 'ok');
        const line = `Point: ${resolved.label}`;
        // The shared commandLog store IS the bridge to the bottom command
        // panel (2D subscribes to it directly) — no event mirror.
        appendLog([line, '']);
        setBuffer('');
        setSuggestions([]);
        return true;
    };

    const runCommand = (value: string) => {
        pushHistory(value);
        const outcome = executeDraftCommand(value);
        appendLog(outcome.lines);
        if (!outcome.success) {
            setFeedback(outcome.message, 'error');
        } else {
            setFeedback(outcome.message, 'ok');
        }
        setBuffer('');
        setSuggestions([]);
        historyScanRef.current = -1;
    };

    /** Space / Enter: an active CommandInteract step is fed first; else a
     *  point value wins over a command; else exact command; else Space runs
     *  the top suggestion (2D behavior) while Enter only completes it
     *  so the full name is visible before the next press. */
    const submit = (fromSpace: boolean) => {
        const value = useDynamicInputStore.getState().buffer.trim();
        if (!value) return; // empty: let the event reach the active tool
        if (feedInteract(value)) return;
        if (looksLikePoint(value)) {
            commitPoint(value);
            return;
        }
        const list = useDynamicInputStore.getState().suggestions;
        const exact = list.some((s) => topSuggestionName(s).toUpperCase() === value.toUpperCase());
        if (!exact && list.length > 0) {
            if (fromSpace) {
                runCommand(topSuggestionName(list[0]));
            } else {
                acceptSuggestion();
            }
            return;
        }
        runCommand(value);
    };

    const recallHistory = (direction: 'up' | 'down') => {
        const history = useDynamicInputStore.getState().inputHistory;
        if (history.length === 0) return;
        let idx = historyScanRef.current;
        if (direction === 'up') {
            idx = idx < 0 ? history.length - 1 : Math.max(0, idx - 1);
        } else {
            idx = idx < 0 ? -1 : idx + 1;
            if (idx >= history.length) idx = -1;
        }
        historyScanRef.current = idx;
        const value = idx < 0 ? '' : history[idx];
        setBuffer(value);
        refreshSuggestions(value);
    };

    // ---- capture-phase keystroke ownership ------------------------------

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isDynamicInputActive()) return;
            // Never steal keys from focused text fields or modal dialogs —
            // the bottom command line must keep behaving classically.
            const target = e.target as HTMLElement | null;
            if (
                target && (
                    target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable ||
                    target.closest?.('[role="dialog"]')
                )
            ) {
                return;
            }
            if (!dynamicOwnsKeyEvent(e)) {
                // 2D parity: with an empty tooltip, Space confirms the
                // active tool exactly like Enter (whose keydown already
                // reaches the tool listeners untouched). Limited to the
                // canvas/body focus so a focused toolbar button keeps its
                // native Space-activation. Dispatched on document.body so
                // tools receive a normal bubbling Enter.
                const tag = (e.target as HTMLElement | null)?.tagName;
                if (
                    e.key === ' ' &&
                    (tag === 'CANVAS' || tag === 'BODY' || !tag) &&
                    useDynamicInputStore.getState().buffer === ''
                ) {
                    e.preventDefault();
                    e.stopPropagation();
                    document.body.dispatchEvent(
                        new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true })
                    );
                }
                return;
            }

            const stop = () => {
                e.preventDefault();
                e.stopPropagation();
            };

            const current = useDynamicInputStore.getState().buffer;

            // ---- DynBox field entry (Faz 11.3) ----
            // While a point/numeric step shows the balloon, digits/punct feed
            // the focused field, Tab walks the fields, Enter locks (or
            // commits when every field carries a value). Letters still build
            // the command buffer (transparent command names).
            const box = useDynBoxStore.getState();
            if (box.mode !== null) {
                if (e.key === 'Tab') {
                    stop();
                    box.cycleField();
                    return;
                }
                if (box.activeIndex !== null) {
                    if (e.key === 'Enter') {
                        const field = box.fields[box.activeIndex];
                        const filled = box.fields.filter((f) => f.typed).length;
                        // Enter on a typed field locks it and walks on; once
                        // every field carries a value the point commits.
                        if (field?.typed && filled < box.fields.length) {
                            stop();
                            box.lockActive();
                            box.cycleField();
                            return;
                        }
                        stop();
                        commitDynBox();
                        return;
                    }
                    if (e.key === 'Backspace') {
                        stop();
                        box.backspace();
                        return;
                    }
                    if (e.key === 'Escape') {
                        // A started edit resets the fields; otherwise the
                        // tool cancel path runs untouched.
                        if (box.fields.some((f) => f.typed)) {
                            stop();
                            box.startEditing(box.mode);
                            return;
                        }
                    } else if (isDynBoxEntryKey(e.key)) {
                        stop();
                        if (e.key === ',') {
                            // Classic coordinate comma: walk the X/Y pair.
                            box.commaField();
                        } else {
                            box.typeChar(e.key);
                        }
                        return;
                    }
                } else if (isDynBoxEntryKey(e.key)) {
                    // First digit opens field entry on the first field.
                    stop();
                    box.typeChar(e.key);
                    return;
                }
            }

            if (e.key === 'Enter') {
                if (!current) return; // empty buffer → tool confirm path
                stop();
                submit(false);
            } else if (e.key === ' ') {
                if (!current) return;
                stop();
                submit(true);
            } else if (e.key === 'Tab') {
                stop();
                acceptSuggestion();
            } else if (e.key === 'Escape') {
                if (!current) return; // let tools cancel
                stop();
                setBuffer('');
                setSuggestions([]);
            } else if (e.key === 'Backspace') {
                if (!current) return; // let curve-style tools pop points
                stop();
                setBuffer(current.slice(0, -1));
                refreshSuggestions(current.slice(0, -1));
            } else if (e.key === 'ArrowUp') {
                stop();
                recallHistory('up');
            } else if (e.key === 'ArrowDown') {
                stop();
                recallHistory('down');
            } else if (e.key.length === 1 && e.key >= ' ') {
                stop();
                const next = current + e.key;
                setBuffer(next);
                refreshSuggestions(next);
                historyScanRef.current = -1;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
        // submit/recall capture store getState accessors only; stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- registration: tell the engine this workbench owns the keystrokes
    useEffect(() => {
        const store = useDynamicInputStore.getState();
        store.setCaptureActive(true);
        return () => store.setCaptureActive(false);
    }, []);

    const commandMode = isCommandActive() || !!interactPrompt;
    const idleReadout = commandMode && buffer.length === 0;
    const dynBoxOpen = dynMode !== null;
    const visible = dynamicEnabled && (buffer.length > 0 || !!feedback || idleReadout || dynBoxOpen);

    const upper = buffer.toUpperCase();
    const inlineRemainder = suggestions.length > 0
        ? (() => {
            const top = topSuggestionName(suggestions[0]);
            return top.toUpperCase().startsWith(upper) && top.length > buffer.length
                ? top.slice(buffer.length)
                : '';
        })()
        : '';

    // Tracking hint (Faz 11.3): the engaged special snap names itself in the
    // balloon, synced with the OTRACK guide visuals (Faz 4.3/11.4).
    const activeSnapType = useSnapStore((s) => s.activeSnap?.type ?? null);
    const trackingEngaged = useSnapStore((s) => s.trackingGuides.length > 0);
    const hintKey: 'dynBox.hint.perpendicular' | 'dynBox.hint.extension' | 'dynBox.hint.parallel'
        | 'dynBox.hint.tangent' | 'dynBox.hint.tracking' | null =
        activeSnapType === 'perpendicular' || activeSnapType === 'extension'
            || activeSnapType === 'parallel' || activeSnapType === 'tangent'
            ? `dynBox.hint.${activeSnapType}` as 'dynBox.hint.perpendicular'
                | 'dynBox.hint.extension' | 'dynBox.hint.parallel' | 'dynBox.hint.tangent'
            : trackingEngaged ? 'dynBox.hint.tracking' : null;

    return (
        <Box
            ref={tooltipRef}
            position="fixed"
            top={0}
            left={0}
            zIndex={40}
            pointerEvents="none"
            display={visible ? 'block' : 'none'}
            willChange="transform"
        >
            <Flex
                direction="column"
                align="flex-start"
                bg="bg.panel"
                border="1px solid"
                borderColor={buffer ? 'brand.500' : 'border.base'}
                borderRadius="md"
                px={2.5}
                py={1.5}
                gap={0.5}
                shadow="lg"
                fontFamily="mono"
                fontSize="xs"
                maxW="320px"
            >
                {/* Prompt head (Faz 11.3): point/pick steps name the balloon. */}
                {interactPrompt ? (
                    <Text color="text.secondary" fontSize="10px" noOfLines={1} maxW="300px">
                        {interactPrompt}
                    </Text>
                ) : null}
                {/* DynBox fields: label + live/typed value + lock pin. The
                    active field owns the border; locked ones pin amber. */}
                {dynBoxOpen && dynFields.map((field, i) => {
                    const display = field.typed !== null ? field.typed : (liveFieldValue(field.label) ?? '—');
                    const active = dynActiveIndex === i;
                    return (
                        <Flex key={`${field.label}-${i}`} align="center" gap={1}>
                            <Box
                                as="span"
                                px={1.5}
                                py={0.5}
                                borderRadius="sm"
                                border="1px solid"
                                borderColor={active ? 'brand.500' : field.locked ? 'var(--color-badge-locked)' : 'border.base'}
                                bg={active ? 'bg.active' : 'transparent'}
                                display="inline-flex"
                                alignItems="center"
                                gap={1}
                                minW="96px"
                            >
                                <Text as="span" color="text.secondary" fontSize="10px">{field.label}</Text>
                                <Text as="span" color={field.typed !== null ? 'brand.500' : 'text.primary'}>
                                    {dynRelative && field.label === 'X' ? '@' : ''}{display}
                                </Text>
                                {field.locked && <Pin size={9} color="var(--color-badge-locked)" />}
                            </Box>
                        </Flex>
                    );
                })}
                {hintKey ? (
                    <Text color="text.secondary" fontSize="10px" fontStyle="italic">
                        {t(hintKey)}
                    </Text>
                ) : null}
                {buffer ? (
                    <Text as="span" color="text.primary" whiteSpace="pre">
                        {buffer}
                        <Text as="span" color="text.secondary">{inlineRemainder}</Text>
                    </Text>
                ) : !dynBoxOpen && readout ? (
                    <Text color="text.secondary">{readout}</Text>
                ) : null}
                {buffer && readout ? (
                    <Text color="text.secondary" fontSize="10px">{readout}</Text>
                ) : null}
                {feedback ? (
                    <Text color={feedback.tone === 'error' ? 'red.400' : 'brand.500'}>
                        {feedback.text}
                    </Text>
                ) : null}
            </Flex>
        </Box>
    );
}
